import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { ChapterLabel, scrollTo, EASE } from "./Motion";
import { EDITORIAL } from "../../data/content";
import { WaitlistCounter } from "../WaitlistCounter";

// ── Single source of truth for hero branding + copy (shared by both layouts) ──
const HERO = {
  alt: "A warm, diverse community of people of different ages, ethnicities and abilities gathered together in conversation, including a wheelchair user, sharing support and belonging",
  label: "Chapter One",
  headline: "You don't have to carry it alone.",
  sub: "A private community where empathy comes before algorithms.",
  badge: "Early Access · Founding Community",
  cta: "Request Early Access",
  invite: "Join before launch to help shape HEARD and receive early access invitations.",
};

// ── Shared full-bleed photograph (responsive crop passed per layout) ──
const HeroImage = ({ objectClass }) => {
  const reduce = useReducedMotion();
  return (
    <motion.img
      src={EDITORIAL.hero}
      alt={HERO.alt}
      fetchPriority="high"
      initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 1.08 }}
      animate={reduce ? { opacity: 1 } : { opacity: 1, scale: 1.01 }}
      transition={{ opacity: { duration: 1.6, ease: "easeOut" }, scale: { duration: 22, ease: "easeOut" } }}
      className={`absolute inset-0 h-full w-full object-cover ${objectClass}`}
      style={{ filter: "contrast(1.06) saturate(1.05)" }}
    />
  );
};

// ── Shared, controlled overlays. Lighter reading gradient so the people in the
//    photograph stay clearly visible while headline text remains readable. ──
const HeroScrims = ({ variant }) => {
  const reading =
    variant === "mobile"
      ? "absolute inset-0 bg-gradient-to-r from-cream/90 via-cream/45 to-cream/5"
      : "absolute inset-0 bg-gradient-to-r from-cream/85 via-cream/35 to-transparent";
  return (
    <>
      <div className="absolute inset-0 mix-blend-multiply bg-gradient-to-br from-[#3a2a15]/6 via-transparent to-[#2f4b39]/6" />
      <div className={reading} />
      <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-cream/70 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-cream/35 to-transparent" />
    </>
  );
};

// ── Shared copy stack; `variant` tunes sizing/spacing per layout ──
const HeroCopy = ({ variant }) => {
  const reduce = useReducedMotion();
  const rise = (delay) => ({
    initial: reduce ? { opacity: 0 } : { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 1.05, ease: EASE, delay },
  });
  const isMobile = variant === "mobile";

  return (
    <>
      <motion.div {...rise(0.15)}>
        <ChapterLabel index={HERO.label} />
      </motion.div>
      <motion.h1
        {...rise(0.28)}
        className={
          isMobile
            ? "serif-display mt-6 max-w-[15ch] text-[2.25rem] leading-[1.08] font-normal text-charcoal"
            : "serif-display mt-8 max-w-3xl text-[3.3rem] lg:text-[4.7rem] leading-[1.05] font-normal text-charcoal"
        }
      >
        {HERO.headline}
      </motion.h1>
      <motion.p
        {...rise(0.42)}
        className={
          isMobile
            ? "mt-5 max-w-[26ch] text-base font-normal leading-relaxed text-ink"
            : "mt-7 max-w-lg text-lg lg:text-xl font-light leading-relaxed text-ink"
        }
      >
        {HERO.sub}
      </motion.p>
      <motion.div {...rise(0.52)} className={isMobile ? "mt-6" : "mt-8"}>
        <span
          data-testid="founding-badge-hero"
          className="inline-flex items-center gap-2 rounded-full border border-sage-deep/25 bg-cream/70 px-4 py-2 text-[0.62rem] sm:text-[0.68rem] font-semibold uppercase tracking-[0.16em] sm:tracking-[0.2em] text-sage-deep backdrop-blur-sm"
        >
          <Sparkles size={14} strokeWidth={1.8} className="text-sage" />
          {HERO.badge}
        </span>
      </motion.div>
      <motion.button
        {...rise(0.62)}
        onClick={() => scrollTo("beta")}
        data-testid="hero-join-beta"
        className={
          isMobile
            ? "group mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-sage-deep px-8 py-4 text-sm uppercase tracking-[0.18em] text-cream shadow-[0_14px_34px_rgba(47,75,57,0.28)] transition-colors duration-500 hover:bg-sage"
            : "group mt-8 inline-flex w-fit items-center gap-2.5 rounded-full bg-sage-deep px-8 py-4 text-sm uppercase tracking-[0.2em] text-cream shadow-[0_14px_34px_rgba(47,75,57,0.28)] transition-all duration-500 hover:bg-sage hover:scale-[1.03]"
        }
      >
        {HERO.cta}
        <ArrowRight size={16} className="transition-transform duration-500 group-hover:translate-x-1" />
      </motion.button>
      <motion.p
        {...rise(0.72)}
        className={
          isMobile
            ? "mt-5 max-w-[32ch] text-sm font-normal leading-relaxed text-ink/75"
            : "mt-5 max-w-md text-sm font-light leading-relaxed text-ink/75"
        }
      >
        {HERO.invite}
      </motion.p>
      <motion.div {...rise(0.8)}>
        <WaitlistCounter variant="hero" />
      </motion.div>
    </>
  );
};

// ── Independent DESKTOP layout (>= lg). Content in normal flow drives the
//    section height (min 100svh), so it can never clip on short viewports. ──
const HeroDesktop = () => (
  <div className="hidden lg:block relative" data-testid="hero-desktop">
    <HeroImage objectClass="object-center" />
    <HeroScrims variant="desktop" />
    <div className="relative z-10 min-h-[100svh] mx-auto max-w-[1400px] px-10 flex flex-col justify-center pt-36 pb-20">
      <HeroCopy variant="desktop" />
    </div>
  </div>
);

// ── Independent MOBILE / TABLET layout (< lg). The photograph sits BEHIND the
//    text (zoomed out so the central group stays visible and uncropped). Only a
//    light, localized cream gradient on the left keeps the wording readable —
//    no full overlay, so the people stay clear and colorful. Height is
//    content-driven (min-h grows) so nothing is ever clipped in portrait. ──
const HeroMobile = () => (
  <div className="lg:hidden relative bg-cream overflow-hidden" data-testid="hero-mobile">
    <img
      src={EDITORIAL.hero}
      alt={HERO.alt}
      fetchPriority="high"
      className="absolute inset-0 h-full w-full object-contain object-[center_28%]"
      style={{ transform: "scale(1.12)", filter: "contrast(1.05) saturate(1.08)" }}
    />
    {/* light, localized reading gradient — primarily left, fades out to reveal people */}
    <div className="absolute inset-0 bg-gradient-to-r from-cream/85 via-cream/48 to-transparent" />
    <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-cream/68 to-transparent" />
    <div className="relative z-10 min-h-[100svh] px-6 sm:px-10 flex flex-col justify-center pt-28 pb-16">
      <HeroCopy variant="mobile" />
    </div>
  </div>
);

export const Ch1Hero = () => (
  <section id="home" className="relative w-full overflow-hidden bg-cream">
    <HeroDesktop />
    <HeroMobile />
  </section>
);
